import { CloudUploadOutlined } from "@ant-design/icons"
import { Flex, Typography } from "antd"
import React, { useEffect, useRef, useState } from "react"

interface FileDragOverlayProps {
  message?: string
  acceptedExtensions?: string[]
  maxFileSize?: number
  onFiles: (files: File[]) => void
}

export const FileDragOverlay: React.FC<FileDragOverlayProps> = ({
  message = "Drop files here to upload",
  acceptedExtensions,
  maxFileSize,
  onFiles
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const dragCounter = useRef(0)

  useEffect(() => {
    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault()
      if (!e.dataTransfer?.types?.includes("Files")) return
      dragCounter.current++
      setIsDragging(true)
    }

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault()
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy"
    }

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault()
      dragCounter.current--
      if (dragCounter.current === 0) setIsDragging(false)
    }

    const handleDrop = (e: DragEvent) => {
      e.preventDefault()
      dragCounter.current = 0
      setIsDragging(false)
      const files = Array.from(e.dataTransfer?.files ?? [])
      if (files.length) onFiles(files)
    }

    document.body.addEventListener("dragenter", handleDragEnter)
    document.body.addEventListener("dragover", handleDragOver)
    document.body.addEventListener("dragleave", handleDragLeave)
    document.body.addEventListener("drop", handleDrop)

    return () => {
      document.body.removeEventListener("dragenter", handleDragEnter)
      document.body.removeEventListener("dragover", handleDragOver)
      document.body.removeEventListener("dragleave", handleDragLeave)
      document.body.removeEventListener("drop", handleDrop)
    }
  }, [onFiles])

  if (!isDragging) return null

  return (
    <div className="absolute inset-0 z-[9999] bg-primary/10 backdrop-blur-sm pointer-events-none">
      <div className="absolute inset-4 border-4 border-dashed border-primary rounded-lg flex items-center justify-center">
        <Flex vertical align="center" gap={16}>
          <CloudUploadOutlined className="text-6xl text-primary" />
          <Typography.Title level={3} className="!mb-0 text-primary">
            {message}
          </Typography.Title>
          {acceptedExtensions && acceptedExtensions.length > 0 && (
            <Typography.Text type="secondary" className="text-base">
              Supported: {acceptedExtensions.join(", ")}
              {maxFileSize && ` · Max: ${maxFileSize}MB`} {/* ← додай */}
            </Typography.Text>
          )}
        </Flex>
      </div>
    </div>
  )
}
